import { uniq, xor } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RowDefinition } from './Rows/RowDefinition'

export interface DataTableConfig<T> {
    items: T[]
    keyProp: string
    rowOption?: RowDefinition<T>
    filterRule?: (item: T, query: string) => boolean
    filterFunction?: (item: T) => boolean
    sortRule?: (item_a: T, item_b: T) => number
    onSelected?: (dataIds: string[]) => void
}
export const isSelectable = (e: any, rowOption: RowDefinition<any>) => {
    return (
        (rowOption?.selectable?.(e) ?? true) &&
        !(rowOption?.disabledSelect?.(e) ?? false)
    )
}
export const useDataTable = function <T>({
    items,
    keyProp,
    rowOption,
    filterRule,
    filterFunction,
    sortRule,
    onSelected,
}: DataTableConfig<T>) {
    const [selected, setSelected] = useState<string[]>([])
    const [queryText, setQueryText] = useState<string>()
    const filterRuleRef = useRef<any>(filterRule)
    const sortRuleRef = useRef<any>(sortRule)
    const rowOptionResolved = useMemo(() => rowOption || {}, [rowOption])

    const onSelect = useCallback(
        (key: string) => {
            // Only call setSelected if the selected item is selectable.
            const selectedItem = items.find((i) => (i as any)[keyProp] === key)
            if (selectedItem && isSelectable(selectedItem, rowOptionResolved)) {
                const newSelectedData = xor(selected, [key])
                setSelected(newSelectedData)
                onSelected?.(newSelectedData)
            }
        },
        [items, keyProp, onSelected, rowOptionResolved, selected],
    )

    const filteredData = useMemo(
        () =>
            items
                .filter((i) => {
                    const boolResult =
                        !queryText ||
                        !filterRuleRef ||
                        !filterRuleRef.current ||
                        filterRuleRef.current(i, queryText.toLowerCase())

                    return filterFunction
                        ? boolResult && filterFunction(i)
                        : boolResult
                })
                .sort(
                    (i, j) =>
                        !sortRuleRef ||
                        !sortRuleRef.current ||
                        sortRuleRef.current(i, j),
                ),

        [items, filterFunction, queryText],
    )

    const onSelectAll = useCallback(() => {
        const selectable = filteredData.filter((i) =>
            isSelectable(i, rowOptionResolved),
        )
        if (selectable.length <= selected.length) {
            setSelected([])
            onSelected?.([])
        } else {
            const newSelectedData = selectable.map((i) => (i as any)[keyProp])
            setSelected(newSelectedData)
            onSelected?.(newSelectedData)
        }
    }, [filteredData, keyProp, onSelected, rowOptionResolved, selected.length])

    const clearSelect = useCallback(() => {
        setSelected([])
    }, [])

    const selectedNonDup = useMemo(() => {
        return uniq(selected)
    }, [selected])

    useEffect(() => {
        //remove data that was filtered
        setSelected((selected) =>
            selected.filter((a) =>
                filteredData.some((f: any) => f[keyProp] === a),
            ),
        )
    }, [filteredData, keyProp])

    return {
        selected: selectedNonDup,
        filteredData,
        queryText,
        onSelect,
        onSelectAll,
        clearSelect,
        setQueryText,
        setSelected,
    }
}
